import React, { useState } from 'react';
import { useEffect, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { Search, Calendar, CheckCircle, X, Clock, Phone, Mail, MessageCircle, Linkedin, FileText, Play, Globe, User, AlertCircle, Pencil, Loader2, Video } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { useMeetings } from '../../hooks/useSupabaseLeads';
import MeetingDetailsModal from './MeetingDetailsModal';
import MeetingStatusDropdown from './MeetingStatusDropdown';
import RescheduleMeetingModal from './RescheduleMeetingModal';
import TranscriptViewerModal from './TranscriptViewerModal';
import RecordingPlayerModal from './RecordingPlayerModal';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import StatsCard from '../Dashboard/StatsCard';
import LoadingScreen from '../shared/LoadingScreen';
import { meetingService } from '../../services/meetingService';

const MeetingsPage: React.FC = () => {
  const tz = useTimezone();
  const { data: allMeetings, isLoading, error, refetch } = useMeetings();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [showMeetingDetailsModal, setShowMeetingDetailsModal] = useState(false);
  const [rescheduleMeeting, setRescheduleMeeting] = useState<any>(null);
  const [transcriptMeeting, setTranscriptMeeting] = useState<any>(null);
  const [recordingMeeting, setRecordingMeeting] = useState<any>(null);
  const itemsPerPage = 10;

  const salesPersons = useMemo(() => {
    const names = new Set<string>();
    allMeetings.forEach(m => { if (m.sales_person) names.add(m.sales_person); });
    return Array.from(names).sort();
  }, [allMeetings]);

  const channels = useMemo(() => {
    const channelSet = new Set<string>();
    allMeetings.forEach(m => { if (m.booked_channel) channelSet.add(m.booked_channel); });
    return Array.from(channelSet).sort();
  }, [allMeetings]);

  const filteredMeetings = allMeetings.filter(meeting => {
    const matchesSearch = (meeting.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (meeting.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (meeting.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (meeting.meeting_status || '').toLowerCase() === statusFilter.toLowerCase();
    const matchesPerson = personFilter === 'all' || meeting.sales_person === personFilter;
    const matchesChannel = channelFilter === 'all' || meeting.booked_channel === channelFilter;
    return matchesSearch && matchesStatus && matchesPerson && matchesChannel;
  });

  const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedMeetings = useMemo(() => {
    return filteredMeetings.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredMeetings, itemsPerPage, startIndex]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, personFilter, channelFilter]);

  const totalMeetings = allMeetings.length;
  const attendedMeetings = allMeetings.filter(meeting => (meeting.meeting_status || '').toLowerCase() === 'attended').length;
  const missedMeetings = allMeetings.filter(meeting => (meeting.meeting_status || '').toLowerCase() === 'missed').length;
  const upcomingMeetings = allMeetings.filter(meeting => (meeting.meeting_status || '').toLowerCase() === 'upcoming').length;

  const getAssignedToTag = (salesPerson: string) => {
    if (!salesPerson) return <span className="text-gray-400 text-sm font-medium">Unassigned</span>;

    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#1875FF]/10 text-[#1875FF] border-2 border-[#1875FF]/30 whitespace-nowrap">
        <User size={12} className="mr-1 flex-shrink-0" />
        {salesPerson}
      </span>
    );
  };

  const getChannelTag = (channel: string) => {
    const normalized = (channel || '').toLowerCase().trim();

    if (normalized.includes('call')) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#1875FF]/10 text-[#1875FF] border-2 border-[#1875FF]/30">
          <Phone size={12} className="mr-1" />
          Call
        </span>
      );
    }
    if (normalized.includes('whatsapp')) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#25D366]/10 text-[#25D366] border-2 border-[#25D366]/30">
          <MessageCircle size={12} className="mr-1" />
          WhatsApp
        </span>
      );
    }
    if (normalized.includes('email')) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border-2 border-orange-200">
          <Mail size={12} className="mr-1" />
          Email
        </span>
      );
    }
    if (normalized.includes('linkedin')) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#0A66C2]/10 text-[#0A66C2] border-2 border-[#0A66C2]/30">
          <Linkedin size={12} className="mr-1" />
          LinkedIn
        </span>
      );
    }
    if (normalized.includes('website') || normalized.includes('web') || normalized.includes('inbound')) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30">
          <Globe size={12} className="mr-1" />
          Website
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border-2 border-gray-200">
        {channel || 'Unknown'}
      </span>
    );
  };

  const formatDateTime = (date: string, time: string, meetingTz?: string) => {
    if (!date) return { date: 'N/A', time: 'N/A' };
    try {
      const timePart = time ? time.substring(0, 5) : '00:00';
      const naiveIso = `${date}T${timePart}:00`;

      if (meetingTz && meetingTz.trim()) {
        // Convert prospect-local wall-clock time to UTC, then format in user's tz
        const utcGuess = new Date(naiveIso + 'Z');
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: meetingTz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });
        const parts = formatter.formatToParts(utcGuess);
        const get = (type: string) => parts.find(p => p.type === type)?.value || '0';
        const renderedInMeetingTz = new Date(
          `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`
        );
        const offsetMs = renderedInMeetingTz.getTime() - utcGuess.getTime();
        const utcInstant = new Date(utcGuess.getTime() - offsetMs);

        const formattedDate = formatInTz(utcInstant, 'MMM dd, yyyy', tz);
        const formattedTime = formatInTz(utcInstant, 'h:mm a', tz);
        return { date: formattedDate, time: formattedTime };
      }

      // No meeting timezone stored - format nicely but cannot convert timezone
      const fallbackDate = formatInTz(new Date(naiveIso + 'Z'), 'MMM dd, yyyy', 'UTC');
      const hour = parseInt(timePart.split(':')[0], 10);
      const minute = timePart.split(':')[1];
      const h = hour % 12 || 12;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const fallbackTime = `${h}:${minute} ${ampm}`;
      return { date: fallbackDate, time: fallbackTime };
    } catch {
      return { date: date, time: time || 'N/A' };
    }
  };

  const handleRowClick = (meeting: any) => {
    setSelectedMeeting(meeting);
    setShowMeetingDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowMeetingDetailsModal(false);
    setSelectedMeeting(null);
  };

  if (isLoading && allMeetings.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 mb-6 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard title="Meetings Booked" value={totalMeetings} icon={Calendar} color="blue" />
        <StatsCard title="Attended" value={attendedMeetings} icon={CheckCircle} color="green" />
        <StatsCard title="Missed" value={missedMeetings} icon={X} color="red" />
        <StatsCard title="Upcoming" value={upcomingMeetings} icon={Clock} color="blue" />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search companies or contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <SimpleCustomSelect
              value={personFilter}
              onChange={setPersonFilter}
              options={[
                { value: 'all', label: 'All Sales People' },
                ...salesPersons.map(name => ({ value: name, label: name }))
              ]}
              placeholder="All Sales People"
              className="w-48"
            />
            <SimpleCustomSelect
              value={channelFilter}
              onChange={setChannelFilter}
              options={[
                { value: 'all', label: 'All Channels' },
                ...channels.map(ch => ({ value: ch, label: ch }))
              ]}
              placeholder="All Channels"
              className="w-48"
            />
            <SimpleCustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'attended', label: 'Attended' },
                { value: 'missed', label: 'Missed' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
              placeholder="All Statuses"
              className="w-48"
            />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-left pl-6 pr-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Company
                </th>
                <th className="text-left px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Person
                </th>
                <th className="text-center px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Date & Time
                </th>
                <th className="text-center px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Assigned To
                </th>
                <th className="text-center px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Status
                </th>
                <th className="text-center px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Transcript & Recording
                </th>
                <th className="text-center px-4 py-4 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                  Booked Channel
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {paginatedMeetings.map((meeting) => {
                const { date, time } = formatDateTime(meeting.meeting_date, meeting.meeting_time, meeting.timezone);
                return (
                  <tr
                    key={meeting.meeting_id}
                    className="hover:bg-[#1875FF]/5 transition-colors cursor-pointer"
                    onClick={() => handleRowClick(meeting)}
                  >
                    <td className="pl-6 pr-4 py-4 overflow-hidden">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 mb-1 truncate">
                          {meeting.company_name || 'Unknown Company'}
                        </div>
                        <div className="flex items-center space-x-2">
                          {meeting.company_website && (
                            <a
                              href={meeting.company_website.startsWith('http') ? meeting.company_website : `https://${meeting.company_website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1875FF] hover:text-[#1875FF]/80 transition-colors"
                              title="Visit Website"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe size={15} />
                            </a>
                          )}
                          {meeting.company_linkedin && (
                            <a
                              href={meeting.company_linkedin.startsWith('http') ? meeting.company_linkedin : `https://${meeting.company_linkedin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1875FF] hover:text-[#1875FF]/80 transition-colors"
                              title="LinkedIn Profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin size={15} />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 overflow-hidden">
                      <div className="min-w-0">
                        <div className="flex items-center space-x-1.5 mb-0.5 min-w-0">
                          <span className="font-medium text-gray-900 truncate">
                            {meeting.dm_full_name || 'Unknown'}
                          </span>
                          {meeting.dm_linkedin && (
                            <a
                              href={meeting.dm_linkedin.startsWith('http') ? meeting.dm_linkedin : `https://${meeting.dm_linkedin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1875FF] hover:text-[#1875FF]/80 transition-colors flex-shrink-0"
                              title="LinkedIn Profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin size={13} />
                            </a>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {meeting.dm_title || ''}
                        </div>
                      </div>
                    </td>
                    <td
                      className={`px-4 py-4 ${['upcoming', 'missed', 'cancelled'].includes((meeting.meeting_status || '').toLowerCase()) ? 'cursor-pointer group/date' : ''}`}
                      onClick={(e) => {
                        if (['upcoming', 'missed', 'cancelled'].includes((meeting.meeting_status || '').toLowerCase())) {
                          e.stopPropagation();
                          setRescheduleMeeting(meeting);
                        }
                      }}
                    >
                      <div className="flex flex-col items-center relative">
                        <div className="flex items-center font-semibold text-gray-900 text-sm tabular-nums">
                          <Calendar size={13} className="mr-1.5 text-[#1875FF] flex-shrink-0" />
                          {date}
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-0.5 tabular-nums">
                          <Clock size={13} className="mr-1.5 text-gray-400 flex-shrink-0" />
                          {time}
                          {['upcoming', 'missed', 'cancelled'].includes((meeting.meeting_status || '').toLowerCase()) && (
                            <Pencil size={11} className="ml-1.5 text-[#1875FF] opacity-0 group-hover/date:opacity-100 transition-opacity" />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center">
                        {getAssignedToTag(meeting.sales_person)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center">
                        <MeetingStatusDropdown
                          currentStatus={meeting.meeting_status}
                          meetingId={meeting.meeting_id}
                          meeting={meeting}
                          onStatusChanged={() => refetch()}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        {(() => {
                          const ts = meeting.transcript_status;
                          const tx = meeting.transcript;
                          if (ts === 'processing') {
                            return (
                              <span className="flex items-center space-x-1 text-amber-500 text-sm font-medium">
                                <Loader2 size={14} className="animate-spin" />
                                <span>Processing...</span>
                              </span>
                            );
                          }
                          if (ts === 'failed') {
                            return <span className="text-red-500 text-sm font-medium">Failed</span>;
                          }
                          if (tx && !tx.startsWith('http') && tx.startsWith('[')) {
                            const segments = meetingService.parseTranscript(tx);
                            if (segments.length > 0) {
                              return (
                                <button
                                  className="flex items-center space-x-1 text-[#1875FF] hover:text-[#1875FF]/80 font-semibold text-sm transition-colors"
                                  onClick={(e) => { e.stopPropagation(); setTranscriptMeeting(meeting); }}
                                >
                                  <FileText size={14} />
                                  <span>Transcript</span>
                                </button>
                              );
                            }
                          }
                          if (tx && tx.startsWith('http')) {
                            return (
                              <a href={tx} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-[#1875FF] hover:text-[#1875FF]/80 font-semibold text-sm transition-colors" onClick={(e) => e.stopPropagation()}>
                                <FileText size={14} />
                                <span>Transcript</span>
                              </a>
                            );
                          }
                          return <span className="text-gray-400 text-sm font-medium">No transcript</span>;
                        })()}
                        {(() => {
                          const rs = meeting.recall_status;
                          const rec = meeting.recording;
                          if (rs === 'recording') {
                            return (
                              <span className="flex items-center space-x-1 text-red-500 text-sm font-medium">
                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                <span>Recording...</span>
                              </span>
                            );
                          }
                          if (rec === 'available') {
                            return (
                              <button
                                className="flex items-center space-x-1 text-[#05E0D1] hover:text-[#05E0D1]/80 font-semibold text-sm transition-colors"
                                onClick={(e) => { e.stopPropagation(); setRecordingMeeting(meeting); }}
                              >
                                <Video size={14} />
                                <span>Recording</span>
                              </button>
                            );
                          }
                          if (rec && rec.startsWith('http')) {
                            return (
                              <a href={rec} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-[#05E0D1] hover:text-[#05E0D1]/80 font-semibold text-sm transition-colors" onClick={(e) => e.stopPropagation()}>
                                <Play size={14} />
                                <span>Recording</span>
                              </a>
                            );
                          }
                          if (rec === 'processing') {
                            return (
                              <span className="flex items-center space-x-1 text-gray-400 text-sm font-medium">
                                <Loader2 size={14} className="animate-spin" />
                                <span>Processing…</span>
                              </span>
                            );
                          }
                          return <span className="text-gray-400 text-sm font-medium">No recording</span>;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getChannelTag(meeting.booked_channel)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredMeetings.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-bold font-display">No meetings found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            {searchTerm || statusFilter !== 'all' || personFilter !== 'all' || channelFilter !== 'all' ? 'Try adjusting your filters' : 'Meetings will appear here once they are booked'}
          </div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredMeetings.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {showMeetingDetailsModal && selectedMeeting && (
        <MeetingDetailsModal
          meeting={selectedMeeting}
          onClose={handleCloseModal}
        />
      )}

      {rescheduleMeeting && (
        <RescheduleMeetingModal
          meeting={rescheduleMeeting}
          onClose={() => setRescheduleMeeting(null)}
          onSuccess={() => refetch()}
        />
      )}

      {transcriptMeeting && (
        <TranscriptViewerModal
          isOpen={!!transcriptMeeting}
          onClose={() => setTranscriptMeeting(null)}
          segments={meetingService.parseTranscript(transcriptMeeting.transcript || '')}
          salesPerson={transcriptMeeting.sales_person}
          leadName={transcriptMeeting.dm_full_name}
          companyName={transcriptMeeting.company_name}
          meetingDate={transcriptMeeting.meeting_date ? (() => { try { return formatInTz(parseISO(transcriptMeeting.meeting_date), 'MMM dd, yyyy', tz); } catch { return transcriptMeeting.meeting_date; } })() : undefined}
        />
      )}

      {recordingMeeting && (
        <RecordingPlayerModal
          isOpen={!!recordingMeeting}
          onClose={() => setRecordingMeeting(null)}
          meetingId={recordingMeeting.meeting_id}
          companyName={recordingMeeting.company_name}
          leadName={recordingMeeting.dm_full_name}
          meetingDate={recordingMeeting.meeting_date ? (() => { try { return formatInTz(parseISO(recordingMeeting.meeting_date), 'MMM dd, yyyy', tz); } catch { return recordingMeeting.meeting_date; } })() : undefined}
        />
      )}
    </div>
  );
};

export default MeetingsPage;
